import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Languages, Loader2, Coins, Copy, ArrowRight } from "lucide-react";

const LANGUAGES = [
  { id: "en", label: "English" },
  { id: "nl", label: "Nederlands" },
  { id: "es", label: "Español" },
  { id: "fr", label: "Français" },
  { id: "de", label: "Deutsch" },
  { id: "it", label: "Italiano" },
  { id: "pt", label: "Português" },
];

export default function AITranslate() {
  const { isOwner, refreshBalance } = useAuth();
  const [pricing, setPricing] = useState(null);
  const [text, setText] = useState("");
  const [targetLanguage, setTargetLanguage] = useState("en");
  const [preserveTone, setPreserveTone] = useState(true);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/ai/pricing").then((r) => setPricing(r.data)); }, []);

  const run = async () => {
    if (!text.trim()) return toast.error("Voer tekst in om te vertalen");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/translate", {
        text, target_language: targetLanguage, preserve_tone: preserveTone,
      });
      setResult(data);
      toast.success("Vertaald");
      refreshBalance();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Vertalen mislukt");
    } finally {
      setBusy(false);
    }
  };

  const copyOut = async () => {
    if (!result?.translation) return;
    await navigator.clipboard.writeText(result.translation);
    toast.success("Vertaling gekopieerd");
  };

  return (
    <div className="p-6 md:p-10 lg:p-14 max-w-[1400px]" data-testid="ai-translate-page">
      <div className="mb-10">
        <div className="font-mono-tech text-[10px] text-amber-400 mb-3">AI · TRANSLATE · STUDIO</div>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-light leading-none">
          Elke tekst, <span className="font-bold text-amber-400">elke taal.</span>
        </h1>
        <p className="text-zinc-400 mt-4 max-w-2xl">
          Vertaal listings, posts of scripts. Toon en marketing-intentie blijven behouden.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input */}
        <div className="space-y-4">
          <div className="bg-[#121214] border border-white/5 rounded-lg p-5 space-y-4">
            <div>
              <Label className="font-mono-tech text-[10px] text-zinc-500">BRONTEKST *</Label>
              <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Plak de tekst die je wilt vertalen…"
                rows={10}
                data-testid="translate-source"
                className="mt-2 bg-[#0b0b0d] border-white/10"
              />
              <div className="mt-1 text-[10px] text-zinc-500 text-right">
                {text.length}/8000
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-mono-tech text-[10px] text-zinc-500">DOELTAAL</Label>
                <Select value={targetLanguage} onValueChange={setTargetLanguage}>
                  <SelectTrigger data-testid="translate-target" className="mt-2 bg-[#0b0b0d] border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1e] border-white/10">
                    {LANGUAGES.map((l) => (
                      <SelectItem key={l.id} value={l.id}>{l.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-mono-tech text-[10px] text-zinc-500">TOON</Label>
                <Select value={preserveTone ? "preserve" : "adapt"} onValueChange={(v) => setPreserveTone(v === "preserve")}>
                  <SelectTrigger data-testid="translate-tone" className="mt-2 bg-[#0b0b0d] border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1e] border-white/10">
                    <SelectItem value="preserve">Behoud originele toon</SelectItem>
                    <SelectItem value="adapt">Aanpassen aan doelpubliek</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <Button
            onClick={run}
            disabled={busy || !text.trim()}
            data-testid="translate-run"
            className="w-full h-11 bg-amber-500 hover:bg-amber-600 text-black font-semibold"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" strokeWidth={1.5} />}
            Vertaal
            {!isOwner && pricing && (
              <span className="ml-2 text-[10px] font-mono-tech opacity-75 flex items-center gap-1">
                <Coins className="w-3 h-3" /> {pricing.translate_credits}
              </span>
            )}
          </Button>
        </div>

        {/* Output */}
        <div>
          {result?.translation ? (
            <div className="bg-[#121214] border border-white/5 rounded-lg p-6" data-testid="translate-result">
              <div className="flex items-center justify-between mb-3">
                <Label className="font-mono-tech text-[10px] text-zinc-500">
                  VERTALING · {(result.language || targetLanguage).toUpperCase()}
                </Label>
                <button
                  onClick={copyOut}
                  data-testid="translate-copy"
                  className="text-xs font-mono-tech text-amber-400 hover:text-amber-300 flex items-center gap-1.5"
                >
                  <Copy className="w-3.5 h-3.5" strokeWidth={1.5} /> Kopieer
                </button>
              </div>
              <p className="text-zinc-100 leading-relaxed whitespace-pre-wrap">{result.translation}</p>
            </div>
          ) : (
            <div className="border border-dashed border-white/10 rounded-lg p-12 text-center h-full flex flex-col items-center justify-center">
              <Languages className="w-10 h-10 text-amber-400 mb-4" strokeWidth={1.5} />
              <div className="text-sm text-zinc-400">De vertaalde tekst verschijnt hier.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
